import React, { useEffect } from "react";
import Footer from "../Templete/Footer";
import Header from "../Templete/Header";
import { Link } from "react-router-dom";

const AboutUs = () => {
  // Set SEO title and meta description
  useEffect(() => {
    document.title = "About Formax Pay | Secure Payment Solutions Provider";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        "content",
        "Learn about Formax Pay - a leading payment solutions provider offering secure, reliable, and innovative payment services for businesses of all sizes."
      );
    }

    // Add structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "About Formax Pay",
      description:
        "Formax Pay is a leading payment solutions provider offering secure, reliable, and innovative digital payment services.",
      url: window.location.href,
      publisher: {
        "@type": "Organization",
        name: "Formax Pay",
        description: "Secure Payment Solutions Provider",
        url: "https://formaxpay.com",
        foundingDate: "2018",
      },
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const features = [
    {
      icon: "uil uil-shield-check",
      title: "Secure Transactions",
      description:
        "End-to-end encrypted transactions with compliance-ready security architecture",
    },
    {
      icon: "uil uil-bolt",
      title: "Instant Processing",
      description:
        "High-performance APIs enabling fast and reliable transaction processing",
    },
    {
      icon: "uil uil-mobile-android",
      title: "Mobile First",
      description:
        "Optimized experiences across mobile, web, and partner platforms",
    },
    {
      icon: "uil uil-analytics",
      title: "Advanced Analytics",
      description:
        "Real-time dashboards, settlement reports, and transaction insights",
    },
    {
      icon: "uil uil-globe",
      title: "Pan-India Reach",
      description:
        "Payment and service coverage across India with scalable infrastructure",
    },
    {
      icon: "uil uil-headphones",
      title: "24/7 Support",
      description:
        "Dedicated technical and operational support for partners and merchants",
    },
  ];

  const values = [
    {
      icon: "uil uil-shield-check",
      title: "Integrity",
      description: "Transparent operations with strict compliance standards",
      color: "primary",
    },
    {
      icon: "uil uil-lightbulb",
      title: "Innovation",
      description: "Constantly evolving our APIs and platforms",
      color: "success",
    },
    {
      icon: "uil uil-users-alt",
      title: "Customer First",
      description: "We build solutions around real business needs",
      color: "warning",
    },
    {
      icon: "uil uil-award",
      title: "Excellence",
      description: "Reliability and performance in every transaction",
      color: "info",
    },
  ];

  const stats = [
    { value: "1000+", label: "Active Merchants & Partners" },
    { value: "₹50Cr+", label: "Monthly Transaction Volume" },
    { value: "99.9%", label: "API & Platform Uptime" },
    { value: "24/7", label: "Technical Support" },
  ];

  return (
    <>
      <Header />

      {/* Hero Section */}
      <section
        className="bg-half-170 d-table w-100"
        style={{
          background:
            "linear-gradient(135deg, rgba(74, 108, 247, 0.05) 0%, rgba(74, 108, 247, 0.02) 100%)",
        }}
      >
        <div className="container">
          <div className="row mt-5 justify-content-center">
            <div className="col-lg-10 text-center">
              <div className="pages-heading">
                <h1 className="title mb-4 display-4 fw-bold">
                  About <span className="text-primary">Formax Pay</span>
                </h1>
                <p className="lead text-muted mb-4">
                  Building reliable, secure, and scalable payment infrastructure
                  that enables businesses, fintechs, and platforms to grow in
                  India’s digital economy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section py-0">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div
                className="card shadow-lg border-0"
                style={{
                  marginTop: "-80px",
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                <div className="card-body p-5 pb-50">
                  <div className="row">
                    {stats.map((stat, index) => (
                      <div
                        className="col-lg-3 col-md-6 col-6 text-center mb-4 mb-lg-0"
                        key={index}
                      >
                        <div className="position-relative">
                          <div className="icon text-center mx-auto mb-3">
                            <div
                              style={{
                                width: "80px",
                                height: "80px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg, rgba(74, 108, 247, 0.1) 0%, rgba(74, 108, 247, 0.05) 100%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto",
                              }}
                            >
                              <h5 className="mb-0 text-primary fw-bold">
                                {stat.value}
                              </h5>
                            </div>
                          </div>
                          <h6 className="mb-0">{stat.label}</h6>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section bg-light">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="position-relative">
                <img
                  src="/images/about-us-3.jpg"
                  className="img-fluid rounded-lg shadow-lg"
                  alt="Formax Pay Team"
                  style={{ borderRadius: "15px" }}
                />
                <div className="position-absolute bottom-0 start-0 p-4">
                  <div
                    className="bg-primary text-white p-3 rounded"
                    style={{ borderRadius: "10px" }}
                  >
                    <h5 className="mb-0">Since 2018</h5>
                    <p className="mb-0 small">
                      Revolutionizing Digital Payments
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 mt-5 mt-lg-0">
              <div className="ms-lg-4">
                <span className="badge bg-primary-soft rounded-pill px-3 py-2 mb-3">
                  Our Journey
                </span>
                <h2 className="mb-4">Pioneering Secure Digital Payments</h2>
                <div className="mb-4">
                  <p className="text-muted mb-3">
                    Founded in 2018,{" "}
                    <strong className="text-primary">Formax Pay</strong> was
                    built with a clear goal — to simplify digital payments and
                    fintech integrations for Indian businesses through reliable
                    technology and compliant infrastructure.
                  </p>
                  <p className="text-muted mb-3">
                    As India’s digital ecosystem expanded, we identified a
                    strong need for scalable APIs that could power payments,
                    recharges, e-governance, and utility services under one
                    unified platform. What began as a small fintech initiative
                    has evolved into a trusted payment and API partner for
                    businesses across sectors.
                  </p>
                  <p className="text-muted">
                    Today, Formax Pay delivers secure, high-performance payment
                    solutions, backed by regulatory compliance, robust APIs, and
                    developer-friendly documentation — helping businesses
                    launch, scale, and operate with confidence.
                  </p>
                </div>

                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="avatar avatar-md bg-primary rounded-circle d-flex align-items-center justify-content-center">
                      <i className="uil uil-check text-white"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="mb-0">
                      Built on compliant, secure, and regulated payment
                      infrastructure
                    </h6>
                    <p className="text-muted small mb-0">
                      Full compliance with all regulatory standards
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="section">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div
                className="card border-0 h-100 shadow-lg"
                style={{
                  borderRadius: "15px",
                  background:
                    "linear-gradient(135deg, #f8f9ff 0%, #f0f3ff 100%)",
                }}
              >
                <div className="card-body p-5">
                  <div className="text-center mb-4">
                    <div className="icon text-center mx-auto mb-4">
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, rgba(74, 108, 247, 0.1) 0%, rgba(74, 108, 247, 0.05) 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                        }}
                      >
                        <i className="uil uil-bullseye h1 mb-0 text-primary"></i>
                      </div>
                    </div>
                    <h3 className="mb-3">Our Mission</h3>
                    <p className="text-muted mb-0">
                      To provide secure, scalable, and compliant payment
                      infrastructure that enables businesses and fintech
                      platforms to build, integrate, and scale digital financial
                      services with ease.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-6 mt-4 mt-lg-0">
              <div
                className="card border-0 h-100 shadow-lg"
                style={{
                  borderRadius: "15px",
                  background:
                    "linear-gradient(135deg, #f0fff4 0%, #e6ffe6 100%)",
                }}
              >
                <div className="card-body p-5">
                  <div className="text-center mb-4">
                    <div className="icon text-center mx-auto mb-4">
                      <div
                        style={{
                          width: "80px",
                          height: "80px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, rgba(25, 135, 84, 0.1) 0%, rgba(25, 135, 84, 0.05) 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                        }}
                      >
                        <i className="uil uil-eye h1 mb-0 text-success"></i>
                      </div>
                    </div>
                    <h3 className="mb-3">Our Vision</h3>
                    <p className="text-muted mb-0">
                      To become a trusted fintech infrastructure partner for
                      businesses across India, known for reliability,
                      compliance, innovation, and developer-first payment
                      solutions.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <span className="badge bg-primary-soft rounded-pill px-3 py-2 mb-3">
                Why Choose Us
              </span>
              <h2 className="mb-4">Why Businesses Choose Formax Pay</h2>
              <p className="text-muted mb-5">
                We combine cutting-edge technology with unparalleled security to
                deliver the best payment experience for your business
              </p>
            </div>
          </div>

          <div className="row g-4">
            {features.map((feature, index) => (
              <div className="col-xl-4 col-lg-6 col-md-6" key={index}>
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <div className="card-body p-4">
                    <div className="d-flex">
                      <div className="flex-shrink-0">
                        <div className="icon text-center me-4">
                          <div
                            style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "12px",
                              background:
                                "linear-gradient(135deg, rgba(74, 108, 247, 0.1) 0%, rgba(74, 108, 247, 0.05) 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <i
                              className={`${feature.icon} h3 mb-0 text-primary`}
                            ></i>
                          </div>
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-2">{feature.title}</h5>
                        <p className="text-muted mb-0 small">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8 text-center">
              <span className="badge bg-primary-soft rounded-pill px-3 py-2 mb-3">
                Our Values
              </span>
              <h2 className="mb-4">Our Core Values</h2>
              <p className="text-muted mb-5">
                These principles guide everything we do at Formax Pay
              </p>
            </div>
          </div>

          <div className="row g-4">
            {values.map((value, index) => (
              <div className="col-lg-3 col-md-6" key={index}>
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{ borderRadius: "15px" }}
                >
                  <div className="card-body p-4 text-center">
                    <div className="icon text-center mx-auto mb-4">
                      <div
                        style={{
                          width: "70px",
                          height: "70px",
                          borderRadius: "50%",
                          background: `linear-gradient(135deg, rgba(var(--bs-${value.color}-rgb), 0.1) 0%, rgba(var(--bs-${value.color}-rgb), 0.05) 100%)`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          margin: "0 auto",
                        }}
                      >
                        <i
                          className={`${value.icon} h2 mb-0 text-${value.color}`}
                        ></i>
                      </div>
                    </div>
                    <h5 className="mb-2">{value.title}</h5>
                    <p className="text-muted small mb-0">{value.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <div
                className="card border-0 shadow-lg"
                style={{
                  borderRadius: "20px",
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)",
                }}
              >
                <div className="card-body p-5">
                  <h2 className="mb-3">
                    Build. Integrate. Scale with Formax Pay.
                  </h2>
                  <p className="text-muted mb-4">
                    Partner with Formax Pay to build secure, scalable, and
                    compliant payment solutions for your business
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-3">
                    <Link
                      to="https://app.formaxpay.com/partner-signup"
                      className="btn btn-primary btn-lg px-4 py-3"
                    >
                      Sign Up Now <i className="fas fa-user-plus ms-2"></i>
                    </Link>

                    <Link
                      to="https://developers.formaxpay.com"
                      className="btn btn-outline-primary btn-lg px-4 py-3"
                    >
                      View API Documentation{" "}
                      <i className="fas fa-book ms-2"></i>
                    </Link>
                  </div>
                  <p className="text-muted small mt-4 mb-0">
                    Simple onboarding • Complete API documentation • Dedicated
                    support
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        :root {
          --bs-primary-rgb: 74, 108, 247;
          --bs-success-rgb: 25, 135, 84;
          --bs-warning-rgb: 255, 193, 7;
          --bs-info-rgb: 13, 202, 240;
        }
        
        .bg-primary-soft {
          background-color: rgba(74, 108, 247, 0.1) !important;
          color: #4a6cf7 !important;
        }
        
        .card {
          transition: all 0.3s ease;
          border: 1px solid rgba(0,0,0,0.05);
        }
        
        .card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 40px rgba(0,0,0,0.1) !important;
        }
        
        .avatar {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .rounded-lg {
          border-radius: 15px !important;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #4a6cf7 0%, #6a11cb 100%);
          border: none;
          transition: all 0.3s ease;
        }
        
        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(74, 108, 247, 0.3);
        }
        
        .btn-outline-primary {
          transition: all 0.3s ease;
        }
        
        .btn-outline-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(74, 108, 247, 0.1);
        }
        
        .lead {
          font-size: 1.25rem;
          font-weight: 300;
        }
        
        .badge {
          font-weight: 500;
          letter-spacing: 0.5px;
        }
        
        .display-4 {
          font-weight: 700;
          line-height: 1.2;
        }
        
        .section {
          padding: 80px 0;
        }
        
        @media (max-width: 768px) {
          .section {
            padding: 60px 0;
          }
          
          .display-4 {
            font-size: 2.5rem;
          }
        }
      `}</style>

      <Footer />
    </>
  );
};

export default AboutUs;
